import type {
    ConditionalFormattingCondition,
    InputValueSizeConditionType,
} from 'owa-conditional-formatting';
import { ConditionalFormattingConditionType } from 'owa-conditional-formatting';
import { isObservableArray } from 'mobx';
import type ImportanceType from 'owa-service/lib/contract/ImportanceType';

export type ConditionalFormattingEvaluationContext = {
    subject: string;
    toMe?: boolean | null;
    ccMe?: boolean | null;
    senderEmail?: string;
    mentionsMe: boolean;
    isRead?: boolean | null;
    hasAttachments?: boolean | null;
    messageSize?: number | null;
    importance?: ImportanceType | null;
    isCopilotPrioritizedHigh?: boolean | null;
    categories?: string[] | null;
    isFlagged?: boolean | null;
    isCompleted?: boolean | null;
};

type Evaluator = (
    condition: ConditionalFormattingCondition,
    ctx: ConditionalFormattingEvaluationContext
) => boolean;

const evaluators: Partial<Record<ConditionalFormattingConditionType, Evaluator>> = {
    [ConditionalFormattingConditionType.From]: (condition, ctx) => {
        const email = (ctx.senderEmail ?? '').toLowerCase();
        const senders = (condition.value as string[]) ?? [];
        if (!email || senders.length === 0) {
            return false;
        }

        // Iterate once; avoid logging here (rule load path already validates)
        for (const sender of senders) {
            if (sender && email === sender.toLowerCase()) {
                return true;
            }
        }
        return false;
    },

    [ConditionalFormattingConditionType.MentionsMe]: (_condition, ctx) => !!ctx.mentionsMe,

    [ConditionalFormattingConditionType.OnTheToOrCcLine]: (_condition, ctx) =>
        !!ctx.toMe || !!ctx.ccMe,

    [ConditionalFormattingConditionType.OnTheToLine]: (_condition, ctx) => !!ctx.toMe,

    [ConditionalFormattingConditionType.OnTheCcLine]: (_condition, ctx) => !!ctx.ccMe,

    [ConditionalFormattingConditionType.NotOnTheToLine]: (_condition, ctx) => ctx.toMe === false,

    [ConditionalFormattingConditionType.SubjectIncludes]: (condition, ctx) =>
        doesTextIncludeAnyKeywords(ctx.subject, (condition.value as string[]) ?? []),

    [ConditionalFormattingConditionType.SenderAddressIncludes]: (condition, ctx) =>
        doesTextIncludeAnyKeywords(ctx.senderEmail ?? '', (condition.value as string[]) ?? []),

    [ConditionalFormattingConditionType.ReadItems]: (_condition, ctx) => ctx.isRead === true,

    [ConditionalFormattingConditionType.UnreadItems]: (_condition, ctx) => ctx.isRead === false,

    [ConditionalFormattingConditionType.WithAttachments]: (_condition, ctx) =>
        ctx.hasAttachments === true,

    [ConditionalFormattingConditionType.WithoutAttachments]: (_condition, ctx) =>
        ctx.hasAttachments === false,

    [ConditionalFormattingConditionType.BetweenSize]: (condition, ctx) => {
        if (!ctx.messageSize) {
            return false;
        }
        const conditionSize = condition.value as InputValueSizeConditionType;
        return ctx.messageSize >= conditionSize.min && ctx.messageSize <= conditionSize.max;
    },

    [ConditionalFormattingConditionType.LowerThanSize]: (condition, ctx) => {
        if (!ctx.messageSize) {
            return false;
        }
        const conditionSize = condition.value as InputValueSizeConditionType;
        return ctx.messageSize <= conditionSize.max;
    },

    [ConditionalFormattingConditionType.GreaterThanSize]: (condition, ctx) => {
        if (!ctx.messageSize) {
            return false;
        }
        const conditionSize = condition.value as InputValueSizeConditionType;
        return ctx.messageSize > conditionSize.min;
    },
    [ConditionalFormattingConditionType.ImportanceHigh]: (_condition, ctx) =>
        ctx.importance === 'High',

    [ConditionalFormattingConditionType.ImportanceNormal]: (_condition, ctx) =>
        ctx.importance === 'Normal',

    [ConditionalFormattingConditionType.ImportanceLow]: (_condition, ctx) =>
        ctx.importance === 'Low',

    [ConditionalFormattingConditionType.ImportanceCopilotPrioritized]: (_condition, ctx) =>
        ctx.isCopilotPrioritizedHigh ?? false,

    [ConditionalFormattingConditionType.FlaggedItems]: (_condition, ctx) => ctx.isFlagged === true,

    [ConditionalFormattingConditionType.UnflaggedItems]: (_condition, ctx) =>
        ctx.isFlagged === false && ctx.isCompleted !== true,

    [ConditionalFormattingConditionType.MarkedAsCompleted]: (_condition, ctx) =>
        ctx.isCompleted === true,

    [ConditionalFormattingConditionType.Categories]: (condition, ctx) => {
        const itemCategories = ctx.categories;
        const conditionCategories = condition.value as string[];
        if (
            !itemCategories ||
            itemCategories.length === 0 ||
            !conditionCategories ||
            conditionCategories.length === 0
        ) {
            return false;
        }

        for (const category of conditionCategories) {
            if (category && !itemCategories.includes(category)) {
                return false;
            }
        }
        return true;
    },
};

export default function isConditionalFormattingConditionSatisfied(
    condition: ConditionalFormattingCondition,
    ctx: ConditionalFormattingEvaluationContext
): boolean {
    const evalFn = evaluators[condition.type as ConditionalFormattingConditionType];

    return evalFn ? evalFn(condition, ctx) : false;
}

function doesTextIncludeAnyKeywords(text: string, keywords: string[]): boolean {
    if (!text || !keywords || !isObservableArray(keywords) || keywords.length === 0) {
        return false;
    }
    const lowered = text.toLowerCase();
    for (const keyword of keywords) {
        if (keyword && lowered.includes(keyword.toLowerCase())) {
            return true;
        }
    }
    return false;
}
